// ---------------------------------------------------------------------------
// Native social sign-in for the SouleMap iOS app.
//
// Inside the app, web popups cannot run OAuth (Google blocks embedded web
// views entirely). Instead, the native shell presents the REAL system
// dialogs — the Face ID "Sign in with Apple" sheet, the native Google
// account picker — via the @capacitor-firebase/authentication plugin, and
// hands the resulting credential to the same Firebase Auth the website uses.
// One account system, three doors.
// ---------------------------------------------------------------------------

import {
  GoogleAuthProvider, OAuthProvider, signInWithCredential,
} from 'firebase/auth';

export function nativeAuthAvailable() {
  try {
    return !!(window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.FirebaseAuthentication);
  } catch (e) {
    return false;
  }
}

/**
 * Present the native sign-in dialog for 'Google' or 'Apple' and sign the
 * web Firebase session in with the returned credential. Resolves to the
 * same UserCredential shape as signInWithPopup, so callers can reuse their
 * post-login logic unchanged.
 */
export async function nativeSocialSignIn(auth, provider) {
  const FA = window.Capacitor.Plugins.FirebaseAuthentication;

  if (provider === 'Google') {
    const result = await FA.signInWithGoogle();
    const idToken = result.credential && result.credential.idToken;
    const accessToken = result.credential && result.credential.accessToken;
    if (!idToken) throw new Error('Google sign-in did not return a credential.');
    const cred = GoogleAuthProvider.credential(idToken, accessToken || undefined);
    return signInWithCredential(auth, cred);
  }

  if (provider === 'Apple') {
    const result = await FA.signInWithApple({ scopes: ['email', 'name'] });
    const idToken = result.credential && result.credential.idToken;
    const rawNonce = result.credential && result.credential.nonce;
    if (!idToken) throw new Error('Apple sign-in did not return a credential.');
    const apple = new OAuthProvider('apple.com');
    const cred = apple.credential({ idToken, rawNonce });
    return signInWithCredential(auth, cred);
  }

  throw new Error(`${provider} sign-in is not available in the app.`);
}
